import { TokenPairSchema } from '@utazzo/shared';
import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { env } from '@/env';
import { installUserActivityTracking, isUserActive } from '@/lib/user-activity';
import { getAccessToken, useAuthStore } from '@/stores/auth-store';

/**
 * Single axios instance for the whole app. Configured via env, never hardcoded.
 *
 * Auth:
 *  - Request interceptor injects `Authorization: Bearer <accessToken>` from the
 *    zustand auth store (synchronous read).
 *  - Response interceptor on 401 attempts a silent refresh using the stored
 *    refresh token (single-flight, shared across concurrent 401s), then retries
 *    the original request once. If refresh fails, the session is cleared so the
 *    router falls back to /login.
 *
 * Errors: the server returns a structured envelope. The interceptor unwraps
 * `error.response.data` and rejects with the envelope so TanStack Query
 * onError handlers can rely on its shape.
 */

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  timeout: 30_000,
  withCredentials: false,
  // Array query params go out as REPEATED KEYS (`?roles=A&roles=B`), not
  // axios's default bracket form (`?roles[]=A&roles[]=B`).
  //
  // The API runs on Express 5, whose default `query parser` is "simple"
  // (Node's `querystring`) rather than Express 4's qs-based "extended".
  // The simple parser has no bracket syntax, so `roles[]=A` arrives as the
  // literal key `"roles[]"` and the DTO's `roles` field is undefined - the
  // filter silently does nothing instead of erroring. Repeated keys parse
  // to an array on multi-select and to a plain string on single-select,
  // which the shared query schemas already accept (they preprocess a
  // comma/single string into an array).
  paramsSerializer: { indexes: null },
});

installUserActivityTracking();

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  // Presence for lead round-robin: only requests made while the person is
  // actually using the tab refresh "at work" (see lib/user-activity.ts).
  config.headers.set('X-User-Active', isUserActive() ? '1' : '0');
  return config;
});

// Single in-flight refresh shared by every request that 401s while it runs.
let refreshInFlight: Promise<string | null> | null = null;

/**
 * Exchange the stored refresh token for a fresh token pair. Uses a bare axios
 * call (not `apiClient`) so it bypasses these interceptors - no recursion, no
 * circular import with auth.api.
 */
const runRefresh = async (): Promise<string | null> => {
  const refreshToken = useAuthStore.getState().tokens?.refreshToken;
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post<unknown>(`${env.VITE_API_BASE_URL}/auth/refresh`, {
      refreshToken,
    });
    const tokens = TokenPairSchema.parse(data);
    useAuthStore.getState().setTokens(tokens);
    return tokens.accessToken;
  } catch {
    return null;
  }
};

const onLoginRoute = (): boolean => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  return path === '/login' || path.startsWith('/forgot') || path.startsWith('/reset');
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;
    const status = error.response?.status;
    const url = original?.url ?? '';
    const isAuthCall = url.includes('/auth/refresh') || url.includes('/auth/login');

    if (status === 401 && original && !original._retried && !isAuthCall) {
      original._retried = true;
      refreshInFlight ??= runRefresh().finally(() => {
        refreshInFlight = null;
      });
      const newToken = await refreshInFlight;
      if (newToken) {
        original.headers.set('Authorization', `Bearer ${newToken}`);
        return apiClient(original);
      }
      if (!onLoginRoute()) {
        useAuthStore.getState().clear();
      }
    }

    if (error.response?.data) {
      return Promise.reject(error.response.data);
    }
    return Promise.reject(error);
  },
);
